import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createContext } from 'ui/lib/react-utils'
import { DEFAULT_SERVICE_WORKER_PATH, FirebaseNotificationClient } from './firebase'
import { getFirebaseConfig } from './firebase/firebase-config'
import { type NotificationMessage, type NotificationsContextValue } from './types'
import { type FirebaseMessagePayload } from './firebase/types'

const [NotificationsContextProvider, useNotificationsContext] =
  createContext<NotificationsContextValue>({
    name: 'Notifications'
  })

export { useNotificationsContext }

interface NotificationsProviderProps {
  children: ReactNode
  isDebug?: boolean
  /** @example '/sw.js' */
  serviceWorkerPath?: string
  /** @remarks Has a priority on serviceWorkerPath param */
  serviceWorkerRegistration?: ServiceWorkerRegistration

  autoRequestToken?: boolean
  autoRequestTokenDelay?: number
}

export const NotificationsProvider: FC<NotificationsProviderProps> = ({
  children,
  isDebug,
  serviceWorkerPath,
  serviceWorkerRegistration,
  autoRequestToken,
  autoRequestTokenDelay = 0
}) => {
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'default'
  )
  const [token, setToken] = useState<string | null>(null)
  const [notification, setNotification] = useState<NotificationMessage | null>(null)
  const [client, setClient] = useState<FirebaseNotificationClient | null>(null)
  const isInitialRequest = useRef(false)
  const firebaseConfig = getFirebaseConfig()
  const isEnabled = !!firebaseConfig

  // Browser-only: Firebase is never initialised during SSR/prerender
  useEffect(() => {
    if (!firebaseConfig) {
      setClient(null)
      return
    }

    setClient(
      new FirebaseNotificationClient(firebaseConfig, {
        isDebug,
        serviceWorkerPath,
        serviceWorkerRegistration
      })
    )
  }, [firebaseConfig, isDebug, serviceWorkerPath, serviceWorkerRegistration])

  // With deals switched off the worker isn't built any more, so drop one a previous deploy left behind
  useEffect(() => {
    if (isEnabled || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return
    }

    const swPath = serviceWorkerPath ?? DEFAULT_SERVICE_WORKER_PATH

    void navigator.serviceWorker
      .getRegistrations()
      .then((registrations) =>
        Promise.all(
          registrations
            .filter((registration) => {
              const worker = registration.active ?? registration.waiting ?? registration.installing
              return !!worker && new URL(worker.scriptURL).pathname === swPath
            })
            .map((registration) => registration.unregister())
        )
      )
      .catch(() => {
        /* empty */
      })
  }, [isEnabled, serviceWorkerPath])

  const onMessage = useCallback(
    (cb: (message: NotificationMessage) => void) => {
      if (!client) return () => void 0

      return client.onMessageListener((fcmMessage: FirebaseMessagePayload) => {
        const message: NotificationMessage = {
          id: fcmMessage.data?.id ?? fcmMessage.messageId,
          type: fcmMessage.data?.category === 'DEAL' ? 'offer' : 'other',
          title: fcmMessage.data?.message ?? fcmMessage.notification?.title ?? 'New message',
          body: fcmMessage.notification?.body ?? '',
          data: fcmMessage.data
        }

        cb(message)
      })
    },
    [client]
  )

  const requestToken = useCallback(async () => {
    if (!client) return null

    const fcmToken = await client.requestToken()

    setPermission(Notification.permission)

    if (fcmToken) {
      setToken(fcmToken)
    }

    return fcmToken
  }, [client])

  useEffect(() => {
    if (client && permission === 'granted' && token === null && !isInitialRequest.current) {
      isInitialRequest.current = true
      void requestToken().finally(() => {
        isInitialRequest.current = false
      })
    }

    return () => {
      isInitialRequest.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only once after mounting component
  }, [requestToken])

  useEffect(() => {
    if (!client || !autoRequestToken || token || isInitialRequest.current) {
      return
    }

    const timer = setTimeout(() => {
      isInitialRequest.current = true
      void requestToken().finally(() => {
        isInitialRequest.current = false
      })
    }, autoRequestTokenDelay)

    return () => {
      clearTimeout(timer)
    }
  }, [autoRequestToken, autoRequestTokenDelay, client, permission, requestToken, token])

  useEffect(() => {
    const unsubscribe = onMessage((payload) => {
      setNotification(payload)
    })

    return () => {
      unsubscribe()
    }
  }, [onMessage])

  const value: NotificationsContextValue = {
    isEnabled,
    token,
    notification,
    onMessage,
    requestToken,
    permission
  }

  return <NotificationsContextProvider value={value}>{children}</NotificationsContextProvider>
}
